import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelFrame } from './panel-frame.js';
import type { PanelId, TabId } from './store.js';

// Unified panel shell. Wraps PanelFrame (the drop-target body wrapper)
// with the standard loading / error / empty / toolbar treatments every
// panel was previously reimplementing.
//
//   <PanelShell tabId panelId loading>           — built-in spinner copy
//   <PanelShell tabId panelId error="...">       — built-in error copy
//   <PanelShell tabId panelId empty="Pick one">  — built-in empty copy
//   <PanelShell tabId panelId toolbar={<Bar/>}>  — sticky tool strip on top
//
// Precedence: error > loading > empty > children. The shell never
// renders children when one of the state booleans is set.

export interface PanelShellProps {
  tabId: TabId;
  panelId: PanelId;
  /** Optional sticky toolbar rendered above the body. Inherits chrome
   *  background; panels usually pass their filter / search bar here. */
  toolbar?: ReactNode;
  /** Force the standardised loading state. */
  loading?: boolean;
  /** Force the error state. Pass a string for the displayed message;
   *  passing `true` falls back to the generic panel error copy. */
  error?: boolean | string;
  /** Force the empty state. Pass a string for the displayed message;
   *  passing `true` falls back to the generic empty copy. */
  empty?: boolean | string;
  /** Optional body padding override. Defaults to var(--s-3) so the
   *  table / content inside breathes from the panel outline (matches
   *  the morning-brief panels' look). Panels that absolutely need a
   *  flush body (e.g. an embedded chart that handles its own gutter)
   *  can pass "0". */
  padding?: string;
  children?: ReactNode;
}

export function PanelShell({
  tabId,
  panelId,
  toolbar,
  loading,
  error,
  empty,
  padding,
  children,
}: PanelShellProps) {
  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      {toolbar ? <ToolbarRegion>{toolbar}</ToolbarRegion> : null}
      <BodyRegion padding={padding}>
        {error ? (
          <ErrorRow message={typeof error === 'string' ? error : undefined} />
        ) : loading ? (
          <LoadingRow />
        ) : empty ? (
          <EmptyRow message={typeof empty === 'string' ? empty : undefined} />
        ) : (
          children
        )}
      </BodyRegion>
    </PanelFrame>
  );
}

function ToolbarRegion({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        flexShrink: 0,
        borderBottom: '1px solid var(--hairline)',
        background: 'var(--surface)',
      }}
    >
      {children}
    </div>
  );
}

function BodyRegion({ padding, children }: { padding?: string; children: ReactNode }) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: padding ?? 'var(--s-3)' }}>
      {children}
    </div>
  );
}

function LoadingRow() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: 'var(--s-4)', color: 'var(--text-tertiary)' }}>
      {t('panelShell.loading')}
    </div>
  );
}

function ErrorRow({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div style={{ padding: 'var(--s-4)', color: 'var(--color-negative-500)' }}>
      {message ?? t('panelShell.error')}
    </div>
  );
}

function EmptyRow({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div style={{ padding: 'var(--s-4)', color: 'var(--text-tertiary)' }}>
      {message ?? t('panelShell.empty')}
    </div>
  );
}
